import React, {createContext, useCallback, useContext, useEffect, useState} from "react";
import {useRequest} from "../../../utils/hooks.jsx";
import {
    deliveriesPageSize,
    DeliveryProviderDeliveriesProjection,
    deliveryProviderDriversProjection
} from "./consts.jsx";
import {TableDateSearchContext} from "../../../admin/Context/TableDateSearchContext.jsx";
import {
    changeDeliveryDriver,
    markDeliveryAsDelivered,
    revertDeliveredStatus
} from "../../../api/deliveries/deliveries.js";

export const DeliveryProviderDeliveriesContext = createContext({});

export const DeliveryProviderDeliveriesProvider = ({children}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const {searchDate} = useContext(TableDateSearchContext);
    const [showDeliveredDeliveries, setShowDeliveredDeliveries] = useState(false);
    const [externalDriverModalVisibility, setExternalDriverModalVisibility] = useState(false);
    const [selectedDeliveryId, setSelectedDeliveryId] = useState(null);
    const [selectedDriverId, setSelectedDriverId] = useState(null);

    const [deliveries = {}, isLoading, error, fetchDeliveries] = useRequest(
        "/api/deliveryProviders/deliveries",
        "POST",
        {
            startDate: searchDate[0].valueOf(),
            endDate: searchDate[1].valueOf(),
            page: currentPage,
            pageSize: deliveriesPageSize,
            projection: DeliveryProviderDeliveriesProjection,
            showDeliveredDeliveries
        },
        [currentPage, searchDate, showDeliveredDeliveries],
        !!searchDate
    );

    useEffect(() => {
        setCurrentPage(1);
    }, [searchDate, showDeliveredDeliveries]);

    const [drivers = [], isLoadingDrivers] = useRequest(
        "/api/deliveryProviders/myDrivers",
        "POST",
        {
            projection: deliveryProviderDriversProjection
        },
        []
    );

    const onChangeDeliveryDriver = useCallback(
        async (deliveryId, driverId, syncDeliveries = true, externalDriverData = null) => {
            const response = await changeDeliveryDriver(deliveryId, driverId, externalDriverData);
            if (response?.error) {
                return;
            }

            if (syncDeliveries) {
                fetchDeliveries();
            }
        },
        [fetchDeliveries]
    );

    const onMarkAsDelivered = useCallback(
        async ({deliveryId}) => {
            const response = await markDeliveryAsDelivered(deliveryId);
            if (response?.error) {
                return;
            }

            fetchDeliveries();
        },
        [fetchDeliveries]
    );

    const onRevertDeliveredStatus = useCallback(
        async ({deliveryId}) => {
            const response = await revertDeliveredStatus(deliveryId);
            if (response?.error) {
                return;
            }

            fetchDeliveries();
        },
        [fetchDeliveries]
    );

    const openExternalDriverModal = useCallback((deliveryId, driverId) => {
        setSelectedDeliveryId(deliveryId);
        setSelectedDriverId(driverId);
        setExternalDriverModalVisibility(true);
    }, []);

    const closeExternalDriverModal = useCallback(() => {
        setExternalDriverModalVisibility(false);
        setSelectedDeliveryId(null);
        setSelectedDriverId(null);
    }, []);

    return (
        <DeliveryProviderDeliveriesContext.Provider
            value={{
                drivers,
                deliveries: deliveries?.deliveries,
                totalDeliveriesCount: deliveries?.totalCount,
                currentPage,
                setCurrentPage,
                isLoading,
                onChangeDeliveryDriver,
                onMarkAsDelivered,
                onRevertDeliveredStatus,
                searchDate,
                showDeliveredDeliveries,
                setShowDeliveredDeliveries,
                externalDriverModalVisibility,
                openExternalDriverModal,
                closeExternalDriverModal,
                selectedDeliveryId,
                selectedDriverId
            }}>
            {children}
        </DeliveryProviderDeliveriesContext.Provider>
    );
};
